import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Howl } from 'howler';

import AppState from '@webapp/store/state';
import { UpdateTrackPreview } from '@webapp/store/tracks/action-creators';

import PreviewSlider from './component';

interface TrackPreviewCtrlProps {
  idLow?: string;
  idHigh?: string;
  className?: string;
  trackId: string;
  locked?: boolean;
}

interface StoreProps {
  audioUrl?: string;
  start: number;
  length: number;
  onChange: (start: number, length: number) => void;
}

const mapStateToProps = (state: AppState, props: TrackPreviewCtrlProps) => ({
  audioUrl: state.tracks.tracks[props.trackId].url,
  start: state.tracks.tracks[props.trackId].previewStart || 0,
  length: state.tracks.tracks[props.trackId].previewLength || 60,
});

const mapDispatchToProps = (
  dispatch: Dispatch,
  props: TrackPreviewCtrlProps
) => ({
  onChange: (start: number, length: number) =>
    dispatch(new UpdateTrackPreview(props.trackId, start, length)),
});

export const TrackPreviewCtrl: React.FC<TrackPreviewCtrlProps & StoreProps> = (
  props
) => {
  const playerRef = React.useRef<Howl>();

  const { audioUrl, trackId } = props;
  const [duration, setDuration] = React.useState(0);
  const [playing, setPlaying] = React.useState(false);
  const [start, setStart] = React.useState(props.start);
  const [previewLength, setPreviewLength] = React.useState(props.length);
  const [currentTrack, setCurrentTrack] = React.useState(trackId);

  const seek = (value: number) => {
    if (playerRef.current) {
      playerRef.current.seek(value);
    }
  };

  React.useEffect(() => {
    if (trackId !== currentTrack) {
      setStart(start);
      if ((playerRef.current?.duration() as number) < 60) {
        setPreviewLength(playerRef.current?.duration || 30);
      } else {
        setPreviewLength(previewLength);
      }
      setCurrentTrack(trackId);
    }
  }, [trackId, currentTrack, start, previewLength]);

  React.useEffect(() => {
    if (audioUrl) {
      playerRef.current = new Howl({
        src: [audioUrl],
        html5: true,
        autoplay: false,
      });

      playerRef.current.on('load', () => {
        setDuration(getDuration());
      });
      playerRef.current.on('play', () => setPlaying(true));
      playerRef.current.on('pause', () =>
        setPlaying(playerRef.current?.playing() || false)
      );
      playerRef.current.on('end', () => setPlaying(false));
      if (playerRef.current?.playing()) {
        const seek = (playerRef.current?.seek() as number) || 0;
        if (seek >= start + previewLength) {
          setPlaying(false);
          playerRef.current.seek(start);
        }
      }
    }

    return () => {
      if (playerRef.current) {
        playerRef.current?.off();
        playerRef.current.unload();
      }
    };
  }, [audioUrl, start]);

  React.useEffect(() => {
    seek(start);
  }, [start]);

  const togglePlay = () => {
    if (playerRef.current) {
      if (playerRef.current.playing()) {
        setPlaying(false);
        playerRef.current.pause();
      } else {
        setPlaying(true);
        playerRef.current.play();
      }
    }
  };

  const getDuration = () => {
    return Math.floor(playerRef.current?.duration() || 0);
  };

  const getMaxTime = () => {
    const time = getDuration() - previewLength;

    if (time > 0) {
      return time;
    }

    return 1;
  };

  const handleUpdate = (value: number, length: number) => {
    if (props.locked) {
      return;
    }
    setStart(value);
    playerRef.current?.play();
    setPreviewLength(length);
    props.onChange(value, length);
  };

  return duration ? (
    <PreviewSlider
      idLow={props.idLow}
      idHigh={props.idHigh}
      currentStart={start}
      isPlaying={playing}
      duration={duration}
      currentLength={previewLength}
      togglePlay={togglePlay}
      maxTime={getMaxTime()}
      onUpdate={handleUpdate}
      locked={props.locked}
    />
  ) : null;
};

export default connect(mapStateToProps, mapDispatchToProps)(TrackPreviewCtrl);
